import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { App } from './App.tsx';
import { AuthProvider } from './context/AuthContext.tsx';
import { ThemeProvider } from './context/ThemeContext.tsx';
import { authToken } from './lib/apiClient.ts';
import './styles/globals.css';
// Shepherd.js brand overrides — imported after globals so they win
// when targeting .shepherd-* selectors. See styles/tour.css for theme.
import './styles/tour.css';

/**
 * OJB Studio Hub bridge token forwarder.
 *
 * The Hub embeds this app inside a Microsoft Teams iframe modal and
 * appends a signed JWT to the SPA URL as `?ojb_token=<jwt>`. The
 * receiver lives on the API origin (api-...railway.app), not here,
 * so we redirect the browser to the API's /api/auth/bridge endpoint,
 * preserving the rest of the URL as ?next= so the user lands back
 * on the right page after sign-in. This runs BEFORE React mounts
 * so the SPA never paints a blank "not signed in" frame in between.
 *
 * Bails out if VITE_API_BASE_URL isn't configured (dev shorthand:
 * proxy is on the same origin) - in that case the request is
 * already on the right origin so we just strip the param and let
 * the receiver handle it from the same origin.
 */
(function forwardBridgeToken() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('ojb_token');
  if (!token) return;

  // Build the path the user will land on AFTER bridge sign-in
  // completes. Preserve their original path + remaining query
  // params + fragment (minus ojb_token itself), so a tile that
  // deep-links to /meetings/:id/edit doesn't bounce them to /.
  params.delete('ojb_token');
  const remainingQuery = params.toString();
  const nextPath =
    window.location.pathname +
    (remainingQuery ? `?${remainingQuery}` : '') +
    window.location.hash;

  const apiBase = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');
  const bridgeUrl =
    apiBase +
    '/api/auth/bridge' +
    `?ojb_token=${encodeURIComponent(token)}` +
    `&next=${encodeURIComponent(nextPath || '/')}`;

  // Use replace() so the back-button doesn't loop the user back
  // through the bridge URL (which only has a single-use token).
  window.location.replace(bridgeUrl);
})();

// Capture #authToken from the URL fragment after OAuth callback and stash it
// in sessionStorage (security audit C2 — was localStorage, which kept the
// credential alive for the full 14-day session even across XSS theft and tab
// closes). The fragment is stripped from the URL afterward to keep it out of
// browser history / referrer headers / bookmark exports.
(function captureAuthTokenFragment() {
  const hash = window.location.hash;
  if (!hash) return;
  // Accept both `#authToken=...` and `#something&authToken=...`
  const params = new URLSearchParams(hash.replace(/^#/, ''));
  const token = params.get('authToken');
  if (token) {
    authToken.set(token);
    params.delete('authToken');
    const remainder = params.toString();
    const newHash = remainder ? `#${remainder}` : '';
    window.history.replaceState(null, '', window.location.pathname + window.location.search + newHash);
  }
})();

/**
 * Belt-and-suspenders stale-cache recovery:
 * - Unregisters any service workers that may have been registered by
 *   a previous build (we don't ship a SW today, but a dev-tools
 *   experiment or a past version could have left one behind).
 * - Clears all Cache Storage entries (same reasoning).
 * Runs fire-and-forget on boot; failures are silently ignored.
 */
(function clearStaleCaches() {
  try {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then((regs) => {
        regs.forEach((r) => r.unregister());
      }).catch(() => {});
    }
    if ('caches' in window) {
      caches.keys().then((keys) => {
        keys.forEach((k) => caches.delete(k));
      }).catch(() => {});
    }
  } catch {
    // no-op — cache clearing is a nice-to-have, not required
  }
})();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <BrowserRouter>
          <AuthProvider>
            <App />
          </AuthProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
